<template>
  <div class="carousel-container">
    <Carousel
      :items-to-show="currentItemsToShow"
      :wrap-around="wrapAround"
      :autoplay="autoplay"
      :transition="500"
      :breakpoints="breakpoints"
      v-model="currentPage"
      :gap="gap"
      :snap-align="'start'"
      slideEffect="slide"
      :style="`--vc-slide-gap: ${gap}px`"
      ref="carouselRef"
    >
      <Slide v-for="(item, index) in items" :key="index">
        <div
          class="carousel__item"
          :style="{
            minHeight: itemStyle.minHeight || 'auto',
            width: itemStyle.width || '100%',
            ...itemStyle,
          }"
        >
          <slot :item="item" :index="index"></slot>
        </div>
      </Slide>

      <template #addons>
        <div v-if="showNavigation || showPagination" :class="footerClass">
          <div v-if="showNavigation" class="flex gap-[25.56px]">
            <button
              @click="prevPage"
              class="carousel-nav-btn flex items-center justify-center"
            >
              <span v-html="carouselLeftArrowSvg" />
            </button>
            <button
              @click="nextPage"
              class="carousel-nav-btn flex items-center justify-center"
            >
              <span v-html="carouselRightArrowSvg" />
            </button>
          </div>

          <slot
            name="pagination"
            :current-page="currentPage"
            :total-pages="totalPages"
            :go-to-page="goToPage"
          >
            <div
              v-if="showPagination"
              class="flex gap-[8px] text-[20px] ms-auto"
            >
              <span
                v-for="n in totalPages"
                :key="n"
                @click="goToPage(n - 1)"
                class="w-[12px] h-[12px] rounded-full cursor-pointer transition-all duration-200"
                :class="
                  currentPage === n - 1
                    ? 'bg-btnActive'
                    : 'border border-solid border-btnActive bg-transparent'
                "
              ></span>
            </div>
          </slot>
        </div>
      </template>
    </Carousel>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { Carousel, Slide } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import { carouselLeftArrowSvg, carouselRightArrowSvg } from "@/utils/commonSvg";

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  showNavigation: {
    type: Boolean,
    default: false,
  },
  showPagination: {
    type: Boolean,
    default: false,
  },
  autoplay: {
    type: Number,
    default: 0,
  },
  wrapAround: {
    type: Boolean,
    default: false,
  },
  itemsToShow: {
    type: [Number, String],
    default: 4,
  },
  gap: {
    type: Number,
    default: 24,
  },
  footerClass: {
    type: String,
    default: "flex justify-between items-center w-full cursor-pointer",
  },
  itemStyle: {
    type: Object,
    default: () => ({
      minHeight: "auto",
      width: "100%",
      padding: "0",
      margin: "0",
    }),
  },
  paginationStyle: {
    type: Object,
    default: () => ({
      dotSize: "12px",
      dotGap: "8px",
      activeColor: "var(--btnActive)",
      inactiveColor: "var(--btnActive)",
    }),
  },
  breakpoints: {
    type: Object,
    default: (items) => ({
      2100: {
        itemsToShow: items.length,
        maxItemsToShow: items.length,
      },
      1200: {
        itemsToShow: 4,
        maxItemsToShow: 4,
      },
      768: {
        itemsToShow: 3,
        maxItemsToShow: 3,
      },
      576: {
        itemsToShow: 1,
        maxItemsToShow: 1,
      },
    }),
  },
});
const carouselRef = ref(null);
const currentPage = ref(0);
const currentItemsToShow = ref(props.itemsToShow);
const currentGap = ref(props.gap);

const updateItemsToShow = () => {
  const width = window.innerWidth;
  const breakpointKeys = Object.keys(props.breakpoints).sort((a, b) => b - a);

  for (const breakpoint of breakpointKeys) {
    if (width >= breakpoint) {
      const breakpointConfig = props.breakpoints[breakpoint];
      currentItemsToShow.value = Math.min(
        breakpointConfig.itemsToShow,
        breakpointConfig.maxItemsToShow || props.itemsToShow
      );
      currentGap.value = props.gap;
      break;
    }
  }
};

const totalPages = computed(() => {
  // Add null/undefined checks
  if (!props.items || !Array.isArray(props.items) || props.items.length === 0) {
    return 0;
  }

  const itemsToShow =
    carouselRef.value?.data?.config.itemsToShow || currentItemsToShow.value;
  return Math.ceil(props.items.length / itemsToShow);
});

const goToPage = (page) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value - 1) {
    currentPage.value++;
  } else if (props.wrapAround) {
    currentPage.value = 0;
  }
};

const prevPage = () => {
  if (currentPage.value > 0) {
    currentPage.value--;
  } else if (props.wrapAround) {
    currentPage.value = totalPages.value - 1;
  }
};

onMounted(() => {
  updateItemsToShow();
  window.addEventListener("resize", updateItemsToShow);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateItemsToShow);
});
</script>

<style scoped>
.carousel-container {
  width: 100%;
}

.carousel__item {
  display: flex;
  justify-content: center;
  align-items: stretch;
  height: 100%;
  width: 100%;
}

:deep(.carousel__slide) {
  padding: 0;
  height: auto;
  width: 100%;
}

:deep(.carousel__viewport) {
  overflow: hidden !important;
  width: 100%;
}

:deep(.carousel__items-container) {
  align-items: stretch;
  width: 100%;
}

:deep(.carousel__item) {
  height: 100%;
  width: 100%;
  padding-bottom: 1rem;
}

.carousel-nav-btn {
  background: none;
  border: none;
  font-size: clamp(1rem, 2vw, 1.5rem);
  color: #373a3c;
  cursor: pointer;
  padding: 0 0.5rem;
  transition: opacity 0.2s;
}

.carousel-nav-btn:hover {
  opacity: 0.7;
}
</style>
